import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { adminApi } from '../api/admin-api';
import { clearAuthTokens } from '@/config/axios.config';
import {
  adminLoginRequest,
  adminLogoutRequest,
  adminRefreshTokenRequest,
  adminVerifyTokenRequest,
  adminChangePasswordRequest,
  adminLoginSuccess,
  adminLogoutSuccess,
  adminRefreshTokenSuccess,
  adminVerifyTokenSuccess,
  adminChangePasswordSuccess,
  adminFailure,
  adminClearAuth,
  AdminLoginRequest,
  AdminRefreshTokenRequest,
  AdminChangePasswordRequest,
} from './admin-slice';
import { clearAuth } from './auth-slice';

// Saga para login de administrador
function* adminLoginSaga(
  action: PayloadAction<AdminLoginRequest>
): Generator<any, void, any> {
  try {
    const { credentials } = action.payload;

    const response: any = yield call(adminApi.login, credentials);

    // Clear regular auth state to avoid role conflicts in AuthGuard
    yield put(clearAuth());

    // Dispatch success action (esto guardará la sesión usando sessionService)
    yield put(
      adminLoginSuccess({
        user: response.user,
        token: response.token,
        refreshToken: response.refreshToken,
        permissions: response.permissions,
        role: response.role,
      })
    );

  } catch (error: any) {
    yield put(
      adminFailure(
        error.response?.data?.message ||
          'Error al iniciar sesión como administrador'
      )
    );
  }
}

// Saga para logout de administrador
function* adminLogoutSaga(): Generator<any, void, any> {
  try {
    yield call(adminApi.logout);
  } catch (error) {
    // Continuar con logout aunque falle la llamada al servidor
    } finally {
    // Limpiar tokens de axios
    clearAuthTokens();

    // Dispatch success action (esto limpiará la sesión usando sessionService)
    yield put(adminLogoutSuccess());

    // También limpiar el estado de auth normal para evitar que el AuthGuard
    // y MainLayout usen datos residuales del admin
    yield put(clearAuth());
  }
}

// Saga para refresh token de administrador
function* adminRefreshTokenSaga(
  action: PayloadAction<AdminRefreshTokenRequest>
): Generator<any, void, any> {
  try {
    const { refreshToken } = action.payload;
    const response: any = yield call(adminApi.refreshToken, refreshToken);

    // Dispatch success action (esto actualizará los tokens usando sessionService)
    yield put(
      adminRefreshTokenSuccess({
        token: response.token,
        refreshToken: response.refreshToken,
      })
    );
  } catch (error: any) {
    // Si falla el refresh, limpiar auth
    yield put(adminClearAuth());
    clearAuthTokens();
    yield put(
      adminFailure(
        'Sesión de administrador expirada. Por favor, inicia sesión nuevamente.'
      )
    );
  }
}

// Saga para verificar token de administrador
function* adminVerifyTokenSaga(): Generator<any, void, any> {
  try {
    const response: any = yield call(adminApi.verifyToken);

    if (response.valid && response.user) {
      yield put(adminVerifyTokenSuccess(response.user));
    } else {
      yield put(adminClearAuth());
      clearAuthTokens();
    }
  } catch (error: any) {
    yield put(adminClearAuth());
    clearAuthTokens();
    yield put(adminFailure('Token de administrador inválido'));
  }
}

// Saga para cambiar contraseña de administrador
function* adminChangePasswordSaga(
  action: PayloadAction<AdminChangePasswordRequest>
): Generator<any, void, any> {
  try {
    const { currentPassword, newPassword } = action.payload;
    yield call(adminApi.changePassword, { currentPassword, newPassword });

    // Dispatch success action
    yield put(adminChangePasswordSuccess());
  } catch (error: any) {
    yield put(
      adminFailure(
        error.response?.data?.message || 'Error al cambiar contraseña'
      )
    );
  }
}

// Watcher sagas
export function* watchAdminLogin() {
  yield takeLatest(adminLoginRequest.type, adminLoginSaga);
}

export function* watchAdminLogout() {
  yield takeLatest(adminLogoutRequest.type, adminLogoutSaga);
}

export function* watchAdminRefreshToken() {
  yield takeLatest(adminRefreshTokenRequest.type, adminRefreshTokenSaga);
}

export function* watchAdminVerifyToken() {
  yield takeLatest(adminVerifyTokenRequest.type, adminVerifyTokenSaga);
}

export function* watchAdminChangePassword() {
  yield takeLatest(adminChangePasswordRequest.type, adminChangePasswordSaga);
}

// Root saga para admin
export function* adminSaga() {
  yield takeLatest(adminLoginRequest.type, adminLoginSaga);
  yield takeLatest(adminLogoutRequest.type, adminLogoutSaga);
  yield takeLatest(adminRefreshTokenRequest.type, adminRefreshTokenSaga);
  yield takeLatest(adminVerifyTokenRequest.type, adminVerifyTokenSaga);
  yield takeLatest(adminChangePasswordRequest.type, adminChangePasswordSaga);
}
